import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Activity, MapPin, Clock, Eye, 
  ChevronLeft, RefreshCw, Wifi, WifiOff,
  Globe, TrendingUp, UserCheck, UserX,
  Search, Filter, MoreHorizontal
} from 'lucide-react';
import './OnlineUsersPage.css';

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color = '#3b82f6', subtitle, theme }) => (
  <div className="stat-card" style={{ borderColor: theme.borderColor }}>
    <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
      <Icon size={20} />
    </div>
    <div className="stat-content">
      <div className="stat-value" style={{ color: theme.textPrimary }}>
        {value}
      </div>
      <div className="stat-title" style={{ color: theme.textSecondary }}>
        {title}
      </div>
      {subtitle && (
        <div className="stat-subtitle" style={{ color: theme.textSecondary }}>
          {subtitle}
        </div>
      )}
    </div>
  </div>
);

// User Card Component
const UserCard = ({ user, theme }) => {
  const getLocationDisplay = (location) => {
    if (!location) return 'Unknown';
    if (location.city && location.country) {
      return `${location.city}, ${location.country}`;
    }
    if (location.country) {
      return location.country;
    }
    return 'Unknown';
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const getPageDisplay = (page) => {
    if (!page) return 'Unknown page';
    
    const pageNames = {
      '/fullchat': 'Chat Page',
      '/analytics': 'Analytics',
      '/profile': 'Profile',
      '/admin': 'Admin Panel',
      '/': 'Home Page'
    };

    return pageNames[page] || page;
  };

  return (
    <div className="user-card" style={{ 
      backgroundColor: theme.cardBg, 
      borderColor: theme.borderColor,
      boxShadow: theme.shadow 
    }}>
      <div className="user-avatar">
        {user.first_name?.[0] || user.email?.[0] || 'U'}
      </div>
      
      <div className="user-info">
        <div className="user-name" style={{ color: theme.textPrimary }}>
          {user.first_name} {user.last_name}
        </div>
        <div className="user-email" style={{ color: theme.textSecondary }}>
          {user.email}
        </div>
        <div className="user-location">
          <MapPin size={12} />
          <span style={{ color: theme.textSecondary }}>
            {getLocationDisplay(user.location)}
          </span>
        </div>
      </div>

      <div className="user-activity">
        <div className="activity-time" style={{ color: theme.textSecondary }}>
          {getTimeAgo(user.last_active)}
        </div>
        <div className="activity-page" style={{ color: '#3b82f6' }}>
          {getPageDisplay(user.current_page)}
        </div>
      </div>

      <div className="user-status">
        <div className={`status-indicator ${user.is_online ? 'online' : 'offline'}`}></div>
      </div>

      <button className="user-actions" style={{ color: theme.textSecondary }}>
        <MoreHorizontal size={16} />
      </button>
    </div>
  );
};

// Search and Filter Component
const SearchAndFilter = ({ searchTerm, setSearchTerm, statusFilter, setStatusFilter, theme }) => (
  <div className="search-filter-container" style={{ borderColor: theme.borderColor }}>
    <div className="search-box">
      <Search size={16} style={{ color: theme.textSecondary }} />
      <input
        type="text"
        placeholder="Search users..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ 
          backgroundColor: theme.inputBg, 
          color: theme.textPrimary,
          borderColor: theme.borderColor 
        }}
      />
    </div>
    
    <div className="filter-box">
      <Filter size={16} style={{ color: theme.textSecondary }} />
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        style={{ 
          backgroundColor: theme.inputBg, 
          color: theme.textPrimary,
          borderColor: theme.borderColor 
        }}
      >
        <option value="all">All Users</option>
        <option value="online">Online</option>
        <option value="offline">Offline</option>
        <option value="active">Recently Active</option>
      </select>
    </div>
  </div>
);

// Main Online Users Page Component
const OnlineUsersPage = () => {
  const { theme, currentThemeName } = useTheme();
  const navigate = useNavigate();
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isConnected, setIsConnected] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchOnlineUsers();
    const interval = setInterval(fetchOnlineUsers, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const fetchOnlineUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/analytics/online-users', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch online users');
      }

      const data = await response.json();
      setOnlineUsers(data.users || []);
      setLastUpdate(new Date());
      setIsConnected(true);
    } catch (err) {
      setError(err.message);
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search and status
  const filteredUsers = onlineUsers.filter(user => {
    const matchesSearch = 
      user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'online' && user.is_online) ||
      (statusFilter === 'offline' && !user.is_online) ||
      (statusFilter === 'active' && user.last_active);

    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const stats = {
    totalUsers: onlineUsers.length,
    onlineUsers: onlineUsers.filter(u => u.is_online).length,
    activeUsers: onlineUsers.filter(u => u.last_active).length,
    countries: new Set(onlineUsers.map(u => u.location?.country).filter(Boolean)).size
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ backgroundColor: theme.bgPrimary }}>
        <div className="loading-spinner"></div>
        <p style={{ color: theme.textSecondary }}>Loading online users...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container" style={{ backgroundColor: theme.bgPrimary }}>
        <div className="error-icon">⚠️</div>
        <h3 style={{ color: theme.textPrimary }}>Error Loading Users</h3>
        <p style={{ color: theme.textSecondary }}>{error}</p>
        <button 
          onClick={fetchOnlineUsers}
          className="retry-button"
          style={{ backgroundColor: theme.primary, color: 'white' }}
        >
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="online-users-container" style={{ backgroundColor: theme.bgPrimary }}>
      {/* Header */}
      <div className="page-header" style={{ borderBottomColor: theme.borderColor }}>
        <div className="header-left">
          <button 
            onClick={() => navigate(-1)}
            className="back-button"
            style={{ color: theme.textSecondary }}
          >
            <ChevronLeft size={20} />
          </button>
          <div>
            <h1 style={{ color: theme.textPrimary }}>Online Users</h1>
            <p style={{ color: theme.textSecondary }}>
              Monitor active users and their activities
            </p>
          </div>
        </div>
        
        <div className="header-right">
          <div className="connection-status">
            <div className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}></div>
            <span style={{ color: theme.textSecondary }}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
          
          <button 
            onClick={fetchOnlineUsers}
            className="refresh-button"
            style={{ 
              backgroundColor: theme.cardBg, 
              borderColor: theme.borderColor,
              color: theme.textPrimary 
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <StatCard 
          title="Total Users" 
          value={stats.totalUsers} 
          icon={Users} 
          color="#3b82f6"
          theme={theme}
        />
        <StatCard 
          title="Online Now" 
          value={stats.onlineUsers} 
          icon={Wifi} 
          color="#10b981"
          theme={theme}
        />
        <StatCard 
          title="Recently Active" 
          value={stats.activeUsers} 
          icon={Activity} 
          color="#f59e0b"
          theme={theme}
        />
        <StatCard 
          title="Countries" 
          value={stats.countries} 
          icon={Globe} 
          color="#8b5cf6"
          theme={theme}
        />
      </div>

      {/* Search and Filter */}
      <SearchAndFilter 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        theme={theme}
      />

      {/* Users List */}
      <div className="users-section">
        <div className="section-header" style={{ borderBottomColor: theme.borderColor }}>
          <h3 style={{ color: theme.textPrimary }}>
            Users ({filteredUsers.length})
          </h3>
          <div className="last-update" style={{ color: theme.textSecondary }}>
            Last updated: {lastUpdate.toLocaleTimeString()}
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="no-users" style={{ color: theme.textSecondary }}>
            <Users size={48} />
            <p>No users found matching your criteria</p>
          </div>
        ) : (
          <div className="users-grid">
            {filteredUsers.map((user, index) => (
              <UserCard key={user.id || index} user={user} theme={theme} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnlineUsersPage;
